import { useCallback, useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import { Buscador, SelectFiltro } from '../ui/Filtros';
import { ESTADO_CLIENTE, ESTADO_SUSCRIPCION } from '../ui/estados';
import { apiPanel } from './api';

const PLANES = [
  { valor: 'gratis', etiqueta: 'Gratis' },
  { valor: 'basico', etiqueta: 'Básico' },
  { valor: 'negocio', etiqueta: 'Negocio' },
  { valor: 'empresa', etiqueta: 'Empresa' },
];

// Cartera de clientes. Las columnas son recuentos y estado comercial: no
// hay ninguna que lleve a ver qué guarda un cliente en su almacén, porque
// no existe esa pantalla ni existe el permiso para construirla.
export default function Clientes({ estadoInicial, onAbrir }) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [estado, setEstado] = useState(estadoInicial || '');
  const [plan, setPlan] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const cargar = useCallback(() => {
    const p = new URLSearchParams();
    if (estado) p.set('estado', estado);
    if (plan) p.set('plan', plan);
    if (busqueda.trim()) p.set('busqueda', busqueda.trim());
    setCargando(true);
    apiPanel
      .get(`/clientes?${p}`)
      .then(setFilas)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [estado, plan, busqueda]);

  // La búsqueda espera a que el usuario deje de teclear: sin esto, cada
  // pulsación es una consulta que recalcula recuentos de todos los clientes.
  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 350 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  const columnas = [
    {
      etiqueta: 'Empresa',
      valor: (c) => c.nombre,
      render: (c) => (
        <div>
          <div className="font-semibold text-gray-900">{c.nombre}</div>
          <div className="text-xs text-gray-500">
            {c.rfc || 'Sin RFC'} · {c.tipo_cuenta === 'PERSONAL' ? 'Personal' : 'Empresa'}
          </div>
        </div>
      ),
    },
    {
      etiqueta: 'Estado',
      valor: (c) => c.estado,
      render: (c) => <ChipEstado mapa={ESTADO_CLIENTE} clave={c.estado} />,
    },
    {
      etiqueta: 'Plan',
      valor: (c) => c.plan || '—',
      render: (c) => (
        <div className="flex items-center gap-1.5">
          <span className="capitalize text-gray-800">{c.plan || '—'}</span>
          {c.estado_suscripcion && (
            <ChipEstado mapa={ESTADO_SUSCRIPCION} clave={c.estado_suscripcion} />
          )}
        </div>
      ),
    },
    { etiqueta: 'Usuarios', valor: (c) => c.usuarios, alineado: 'derecha' },
    { etiqueta: 'Sucursales', valor: (c) => c.sucursales, alineado: 'derecha' },
    { etiqueta: 'Productos', valor: (c) => c.productos, alineado: 'derecha' },
    {
      etiqueta: 'Movs. 30 d',
      valor: (c) => c.movimientos_30d,
      alineado: 'derecha',
      render: (c) => (
        <span className={c.movimientos_30d === 0 ? 'text-amber-700 font-semibold' : ''}>
          {c.movimientos_30d}
        </span>
      ),
    },
    {
      etiqueta: 'Último acceso',
      valor: (c) => c.ultimo_acceso || '',
      render: (c) => <TiempoRelativo valor={c.ultimo_acceso} />,
    },
  ];

  if (error) return <p className="text-sm text-red-700">{error}</p>;

  return (
    <ListaEstandar
      titulo="Clientes"
      subtitulo="Recuentos y estado comercial. El inventario de cada empresa no es visible desde aquí."
      filtros={
        <>
          <Buscador valor={busqueda} onChange={setBusqueda} placeholder="Empresa o RFC…" />
          <SelectFiltro
            valor={estado}
            onChange={setEstado}
            vacio="Todos los estados"
            opciones={[
              { valor: 'activa', etiqueta: 'Activas' },
              { valor: 'suspendida', etiqueta: 'Suspendidas' },
              { valor: 'cancelada', etiqueta: 'Canceladas' },
            ]}
          />
          <SelectFiltro valor={plan} onChange={setPlan} vacio="Todos los planes" opciones={PLANES} />
        </>
      }
      columnas={columnas}
      filas={filas}
      cargando={cargando}
      exportar={{ nombre: 'clientes' }}
      acciones={(c) => [{ etiqueta: 'Ver ficha', onClick: () => onAbrir(c.id) }]}
      vacio={
        <EstadoVacio
          icono={Building2}
          titulo="Ningún cliente con esos filtros"
          descripcion="Prueba a quitar el filtro de estado o de plan."
        />
      }
    />
  );
}
